import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import from generated schemas
const { ComplexSaleFirestoreResponse, CreateComplexSaleDto, UpdateComplexSaleDto } = schemas;

// Re-export schemas for external use
export const ComplexSaleResponseSchema = ComplexSaleFirestoreResponse;
export const CreateComplexSaleSchema = CreateComplexSaleDto;
export const UpdateComplexSaleSchema = UpdateComplexSaleDto;

export const DeleteResponseSchema = z.object({
  message: z.string(),
}).strict();

// Type exports
export type ComplexSaleResponse = z.infer<typeof ComplexSaleResponseSchema>;
export type CreateComplexSale = z.infer<typeof CreateComplexSaleSchema>;
export type UpdateComplexSale = z.infer<typeof UpdateComplexSaleSchema>;

// API factory function
export function createComplexSalesApi(client: TypedApiClient) {
  return {
    getComplexSales: (params?: {
      storeId?: string;
      /**
       * A status ID. ATL-1143: matched against the generated `status_id` column, so it selects
       * rows whose status is the Firestore map as well as those holding a bare id.
       */
      status?: string;
      startDate?: string;
      endDate?: string;
    }): Promise<ComplexSaleResponse[]> => {
      return client.get('/complex-sales', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(ComplexSaleResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/complex-sales` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getComplexSalesWindow: (
      params: {
      storeId?: string;
      status?: string;
      clientId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof ComplexSaleResponseSchema>>> => {
      return client.get('/complex-sales/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(ComplexSaleResponseSchema),
      });
    },

    getComplexSaleById: (id: string): Promise<ComplexSaleResponse> => {
      return client.get(apiPath`/complex-sales/${id}`, {
        responseSchema: ComplexSaleResponseSchema,
      });
    },

    createComplexSale: (data: CreateComplexSale): Promise<ComplexSaleResponse> => {
      return client.post('/complex-sales', {
        data,
        responseSchema: ComplexSaleResponseSchema,
      });
    },

    updateComplexSale: (id: string, data: UpdateComplexSale): Promise<ComplexSaleResponse> => {
      return client.patch(apiPath`/complex-sales/${id}`, {
        data,
        responseSchema: ComplexSaleResponseSchema,
      });
    },

    deleteComplexSale: (id: string): Promise<z.infer<typeof DeleteResponseSchema>> => {
      return client.delete(apiPath`/complex-sales/${id}`, {
        responseSchema: DeleteResponseSchema,
      });
    },
  };
}
